/**
 * Signing in as a person the platform knows.
 *
 * The in-browser gate (accounts.ts) proves something only to the browser that
 * holds it: the platform never sees it, so a build using it has to carry a
 * key of its own, and a public site cannot. This is the other door. A person
 * signs in with Google Identity Platform, and every request to the platform
 * carries their ID token, which the platform verifies against the provider's
 * published keys (backend core/tokens.py). Who they are and what they may do
 * is then decided by the platform — their principal and its roles — and
 * nothing in this file grants anything.
 *
 * The SDK is imported on first use, so a build without a provider (the demo
 * artifact) never loads it.
 *
 * The session is kept in sessionStorage, as the in-browser gate's is: closing
 * the tab signs out. Thirty idle minutes do too, checked when the person comes
 * back — the provider's own session would otherwise run until its refresh
 * token is revoked.
 */
import type { Auth, User } from "firebase/auth";
import type { IdentityConfig } from "../../config/env";

export interface PlatformUser {
  /** The provider's subject — what the platform binds a principal to. */
  uid: string;
  email: string | null;
  name: string;
}

const IDLE_MS = 30 * 60_000;
const SEEN_KEY = "slt.idp.seen";

let authPromise: Promise<Auth> | null = null;
let configured: IdentityConfig | null = null;

export function configureIdentity(c: IdentityConfig): void {
  if (configured) return;
  configured = c;
}

async function auth(): Promise<Auth> {
  if (!configured) throw new Error("No identity provider is configured for this build.");
  const c = configured;
  authPromise ??= (async () => {
    const [{ initializeApp }, fa] = await Promise.all([import("firebase/app"), import("firebase/auth")]);
    const app = initializeApp({ apiKey: c.apiKey, authDomain: c.authDomain, projectId: c.projectId }, "sky-logics");
    const a = fa.getAuth(app);
    await fa.setPersistence(a, fa.browserSessionPersistence);
    await a.authStateReady();
    return a;
  })();
  return authPromise;
}

function toUser(u: User): PlatformUser {
  const fromEmail = (u.email ?? "")
    .split("@")[0]
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
  return { uid: u.uid, email: u.email, name: u.displayName || fromEmail || u.uid };
}

function touch(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, String(Date.now()));
  } catch {
    /* storage refused: the idle limit is then the tab's lifetime */
  }
}

function idledOut(): boolean {
  try {
    const seen = Number(sessionStorage.getItem(SEEN_KEY));
    return !!seen && Date.now() - seen > IDLE_MS;
  } catch {
    return false;
  }
}

/**
 * Keep the session alive while the person is working, and end it after
 * thirty minutes without them.
 *
 * The idle time was once measured from the last page load, because the only
 * moments it was recorded were signing in and loading the page: an officer
 * working the interactive map for an hour was signed out by the next reload,
 * mid-shift, with no activity missing at all. Activity is now what counts —
 * a click, a tap, a key, a scroll — heard in the capture phase, before a
 * canvas that handles its own pointer can swallow it; written at most every
 * fifteen seconds; and the check runs while the page is open and when the
 * tab comes back, not only when it reloads.
 */
export function watchActivity(onIdle: () => void): () => void {
  let last = 0;
  const seen = () => {
    const now = Date.now();
    if (now - last < 15_000) return;
    last = now;
    touch();
  };
  const events = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"] as const;
  for (const e of events) window.addEventListener(e, seen, { capture: true, passive: true });
  const check = () => {
    if (idledOut()) onIdle();
  };
  const onVisible = () => {
    if (document.visibilityState === "visible") check();
  };
  document.addEventListener("visibilitychange", onVisible);
  const timer = setInterval(check, 60_000);
  touch();
  return () => {
    for (const e of events) window.removeEventListener(e, seen, { capture: true });
    document.removeEventListener("visibilitychange", onVisible);
    clearInterval(timer);
  };
}

/** The person already signed in on this tab, if their session has not idled out. */
export async function restoreUser(): Promise<PlatformUser | null> {
  const a = await auth();
  if (!a.currentUser) return null;
  if (idledOut()) {
    await signOutOfPlatform();
    return null;
  }
  touch();
  return toUser(a.currentUser);
}

/** Why a sign-in was refused, in words for the person at the screen. */
export class SignInRefused extends Error {}

function explain(err: unknown): SignInRefused {
  const code = (err as { code?: string } | undefined)?.code ?? "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-email":
      return new SignInRefused(
        "That email and password do not match an account. First time here? Your account starts with no password — set one with the link below.",
      );
    case "auth/user-disabled":
      return new SignInRefused("This account has been disabled. Ask your administrator.");
    case "auth/too-many-requests":
      return new SignInRefused("Too many attempts. Wait a few minutes, or reset the password.");
    case "auth/network-request-failed":
      return new SignInRefused("The sign-in service could not be reached. Check the connection and try again.");
    case "auth/multi-factor-auth-required":
      return new SignInRefused(
        "This account asks for a second factor, which this screen cannot take yet. Ask your administrator.",
      );
    default:
      return new SignInRefused(`Sign-in failed (${code || "unknown error"}).`);
  }
}

export async function signInToPlatform(email: string, password: string): Promise<PlatformUser> {
  const a = await auth();
  const { signInWithEmailAndPassword } = await import("firebase/auth");
  try {
    const cred = await signInWithEmailAndPassword(a, email, password);
    touch();
    return toUser(cred.user);
  } catch (err) {
    throw explain(err);
  }
}

/**
 * Send the provider's password-reset email. Also how a person whose account
 * an administrator created sets their first password.
 *
 * Resolves whether or not the address has an account: saying which addresses
 * exist would tell anybody who asks.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  const a = await auth();
  const { sendPasswordResetEmail } = await import("firebase/auth");
  try {
    await sendPasswordResetEmail(a, email);
  } catch (err) {
    const code = (err as { code?: string } | undefined)?.code;
    if (code === "auth/user-not-found") return;
    throw explain(err);
  }
}

export async function signOutOfPlatform(): Promise<void> {
  try {
    sessionStorage.removeItem(SEEN_KEY);
  } catch {
    /* nothing to clear */
  }
  if (!authPromise) return;
  const a = await authPromise;
  const { signOut } = await import("firebase/auth");
  await signOut(a);
}

/**
 * The bearer token for the next request, refreshed by the SDK when it is
 * within five minutes of expiry. Undefined when nobody is signed in, which the
 * platform answers with 401 rather than this code guessing.
 */
export async function platformToken(): Promise<string | undefined> {
  if (!authPromise) return undefined;
  const a = await authPromise;
  if (!a.currentUser) return undefined;
  touch();
  return a.currentUser.getIdToken();
}
